import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, ShieldCheck, Plus, X, Scale } from "lucide-react";
import axios from "axios";
import { FIRM, firmContactLine } from "@/lib/firm";

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtLongDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};

const DEFAULT_REQUESTS = [
  "A complete life-of-loan transaction history (all payments, charges, credits, and disbursements).",
  "The current unpaid principal balance, per-diem interest, and an itemized payoff statement.",
  "The identity of the current owner/holder or assignee of the mortgage loan and the current servicer of record.",
  "Copies of all notes, riders, assignments, and the recorded security instrument for the loan.",
  "Copies of any correspondence log entries relating to the errors asserted above.",
];
const DEFAULT_ERRORS = [
  "Failure to provide an accurate payoff balance in response to a written request.",
  "Failure to respond to prior written correspondence regarding the account.",
];

// Small labelled input for the (no-print) control panel.
const Ctl = ({ label, value, onChange, type = "text", full = false, area = false, placeholder = "" }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    {area ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    )}
  </div>
);

// Editable add/remove list used for both the Notice-of-Error and Request-for-Information items.
const ItemList = ({ label, items, setItems, testid }) => (
  <div className="md:col-span-2" data-testid={testid}>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="text-[#8A8A82] text-[12px] pt-2 w-4 text-right">{i + 1}.</span>
          <textarea rows={2} value={it} data-testid={`${testid}-item-${i}`}
            onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))}
            className="flex-1 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
          <button onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}
            title="Remove" className="mt-1 text-[#9A9A90] hover:text-[#E4646E] p-1"><X size={14} /></button>
        </div>
      ))}
    </div>
    <button onClick={() => setItems([...items, ""])} data-testid={`${testid}-add`}
      className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#E4C97A] hover:text-[#d4a017]">
      <Plus size={13} /> Add item
    </button>
  </div>
);

export default function RespaNotice() {
  const { appId } = useParams();
  const [f, setF] = useState({
    date: todayISO(),
    certified: true,
    trackingNo: "",
    servicerEmail: "",
    servicerName: "",
    servicerAddress: "",
    borrowerName: "",
    loanNo: "",
    propertyAddress: "",
    payoffGoodThrough: plusDaysISO(30),
    contactEmail: "",
    signerName: FIRM.officer || "",
    signerTitle: "Authorized Officer",
  });
  const [errors, setErrors] = useState(DEFAULT_ERRORS);
  const [requests, setRequests] = useState(DEFAULT_REQUESTS);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!appId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/borrower/applications/${appId}`);
        const app = r.data.application || {};
        const b = app.binder || {};
        setF((p) => ({
          ...p,
          borrowerName: b.borrowerName || p.borrowerName,
          loanNo: b.accountNo || p.loanNo,
          propertyAddress: b.propertyAddress || b.borrowerAddress || p.propertyAddress,
          servicerName: b.currentLender || p.servicerName,
          servicerAddress: b.currentLenderAddress || p.servicerAddress,
        }));
      } catch { /* fall back to manual entry */ }
    })();
  }, [appId]);

  return (
    <div className="binder-print-root min-h-screen bg-[#1a1a14] text-[#ECECE4]">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#9A9A90] hover:text-white flex items-center gap-1.5 text-[12px]" data-testid="respa-back-link">
              <ArrowLeft size={14} /> Back
            </Link>
            <Link to="/" className="text-[#9A9A90] hover:text-white text-[12px]" data-testid="respa-home-link">Home</Link>
            <div className="flex items-center gap-2">
              <Scale size={16} className="text-[#B8860B]" />
              <span className="font-serif text-lg">RESPA / Reg X · Notice of Error &amp; Request for Information</span>
            </div>
          </div>
          <button onClick={() => window.print()} data-testid="respa-print-btn"
            className="flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0750a] text-[#13130E] font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
            <Printer size={15} /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Controls (not printed) */}
      <div className="no-print bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="respa-controls">
          <Ctl label="Letter Date" type="date" value={f.date} onChange={set("date")} />
          <Ctl label="Payoff Good-Through" type="date" value={f.payoffGoodThrough} onChange={set("payoffGoodThrough")} />
          <Ctl label="Tracking No. (Certified Mail)" value={f.trackingNo} onChange={set("trackingNo")} placeholder="9407 ..." />
          <Ctl label="Servicer Email (cc)" value={f.servicerEmail} onChange={set("servicerEmail")} placeholder="respa@servicer.com" />

          <Ctl label="Servicer / Institution Name" value={f.servicerName} onChange={set("servicerName")} full />
          <Ctl label="Servicer Designated Notice Address" value={f.servicerAddress} onChange={set("servicerAddress")} area full
            placeholder={"Attn: RESPA Notices\nP.O. Box ...\nCity, ST ZIP"} />

          <Ctl label="Borrower / Obligor Name" value={f.borrowerName} onChange={set("borrowerName")} />
          <Ctl label="Loan / Account No." value={f.loanNo} onChange={set("loanNo")} />
          <Ctl label="Property / Collateral Address" value={f.propertyAddress} onChange={set("propertyAddress")} full />

          <label className="flex items-center gap-2 text-[12px] text-[#B8C4B8] md:col-span-2">
            <input type="checkbox" checked={f.certified} onChange={(e) => set("certified")(e.target.checked)} data-testid="respa-certified-toggle" />
            Show "Certified Mail, Return Receipt Requested" delivery line
          </label>

          <Ctl label="Reply-To Email" value={f.contactEmail} onChange={set("contactEmail")} placeholder="desk@seutrade..." />
          <Ctl label="Signer Name" value={f.signerName} onChange={set("signerName")} />
          <Ctl label="Signer Title" value={f.signerTitle} onChange={set("signerTitle")} />

          <ItemList label="I. Notice of Error — asserted error(s) [12 CFR § 1024.35(b)]" items={errors} setItems={setErrors} testid="respa-errors" />
          <ItemList label="II. Request for Information — items requested [12 CFR § 1024.36(a)]" items={requests} setItems={setRequests} testid="respa-requests" />
        </div>
      </div>

      {/* The letter */}
      <div className="print-area max-w-5xl mx-auto px-6 py-8 flex flex-col items-center gap-8">
        <div className="paper-sheet legal-page" data-testid="respa-letter">
          {/* Letterhead */}
          <div className="rn-head">
            <div className="rn-firm">{FIRM.name}</div>
            <div className="rn-firm-sub">{firmContactLine}</div>
          </div>
          <div className="lg-rule" />

          <div className="rn-meta">
            <div><strong>Date:</strong> {fmtLongDate(f.date)}</div>
            <div><strong>Sent via:</strong> {f.certified ? "Certified Mail, Return Receipt Requested" : "First-Class Mail"}{f.trackingNo ? ` ${f.trackingNo}` : ""}{f.servicerEmail ? ` · and email to ${f.servicerEmail}` : ""}</div>
          </div>

          <div className="rn-block">
            <div className="rn-lbl">To (Servicer — designated address for error notices / information requests):</div>
            <div><strong>{f.servicerName || "____________________"}</strong></div>
            {String(f.servicerAddress || "").split("\n").filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
          </div>

          <div className="rn-re">
            <strong>Re:&nbsp; Notice of Error and Request for Information under RESPA (Regulation X), 12 CFR §§ 1024.35 and 1024.36</strong>
            <table className="lg-table" style={{ marginTop: 8 }}><tbody>
              <tr><td>Borrower / Obligor</td><td>{f.borrowerName || "—"}</td></tr>
              <tr><td>Loan / Account No.</td><td>{f.loanNo || "—"}</td></tr>
              <tr><td>Property / Collateral Address</td><td>{f.propertyAddress || "—"}</td></tr>
            </tbody></table>
          </div>

          <p style={{ marginTop: 14 }}>To Whom It May Concern:</p>
          <p>This letter is a <strong>Notice of Error</strong> pursuant to 12 CFR § 1024.35 and a <strong>Request for Information</strong> pursuant to 12 CFR § 1024.36. It is sent to the address your institution has designated for such notices and requests. This letter does not waive, and expressly reserves, all rights, claims, and defenses.</p>

          <div className="lg-h">I. Notice of Error — 12 CFR § 1024.35(b)</div>
          <p>The following error(s) are asserted with respect to the servicing of the above-referenced loan:</p>
          <ol className="lg-ol" data-testid="respa-errors-print">
            {errors.filter((e) => e && e.trim()).map((e, i) => <li key={i}>{e}</li>)}
          </ol>

          <div className="lg-h">II. Request for Information — 12 CFR § 1024.36(a)</div>
          <p>Please provide the following information reasonably available to you:</p>
          <ol className="lg-ol" data-testid="respa-requests-print">
            {requests.filter((r) => r && r.trim()).map((r, i) => (
              <li key={i}>{r.replace("{payoff_good_through}", fmtLongDate(f.payoffGoodThrough))}
                {r.toLowerCase().includes("payoff statement") ? ` (good through ${fmtLongDate(f.payoffGoodThrough)})` : ""}</li>
            ))}
          </ol>

          <div className="lg-h">III. Servicer Obligations and Timeline</div>
          <ol className="lg-ol">
            <li><strong>Acknowledgment.</strong> Written acknowledgment within <strong>5 business days</strong> of receipt (excluding legal public holidays, Saturdays, and Sundays). 12 CFR §§ 1024.35(d), 1024.36(c).</li>
            <li><strong>Response — Notice of Error.</strong> Correction (or a written explanation of why no error occurred, after a reasonable investigation) not later than <strong>30 business days</strong> after receipt, subject to any permitted 15-business-day extension with notice. 12 CFR § 1024.35(e).</li>
            <li><strong>Response — Request for Information.</strong> The requested information (or a written explanation that it is not available) within the periods set by 12 CFR § 1024.36(d).</li>
            <li><strong>Credit Reporting.</strong> Pursuant to 12 CFR § 1024.35(i), you may not, for <strong>60 days</strong> after receipt of this Notice of Error, furnish adverse information to any consumer reporting agency regarding any payment that is the subject of this Notice.</li>
          </ol>

          <p style={{ marginTop: 12 }}>Please direct all responses to the sender at the address above{f.contactEmail ? ` and to ${f.contactEmail}` : ""}. This notice is submitted in good faith to resolve the matters identified and to obtain the requested information.</p>

          <p style={{ marginTop: 20 }}>Respectfully,</p>
          <div className="rn-sig">
            <div className="rn-sig-line" />
            <div><strong>{f.signerName || "____________________"}</strong>, {f.signerTitle}</div>
            <div className="rn-sig-sub">On behalf of {f.borrowerName || "the Borrower"}</div>
          </div>

          <p className="lg-note">Template for review by licensed counsel before sending. Nothing herein constitutes legal advice. RESPA / Regulation X governs mortgage servicing; it does not obligate any party to accept a private negotiable instrument as payment.</p>
        </div>
      </div>

      <style>{`
        @media print {
          html, body { background:#fff !important; }
          .binder-print-root { background:#fff !important; color:#000 !important; min-height:0 !important; }
          .print-area { max-width:none !important; padding:0 !important; margin:0 !important; }
          .paper-sheet { box-shadow:none !important; margin:0 !important; page-break-after:auto; }
          .legal-page { padding:0.7in 0.8in !important; }
          .lg-ol > li, p { break-inside:avoid !important; page-break-inside:avoid !important; }
          .lg-table, tr { break-inside:avoid !important; page-break-inside:avoid !important; }
        }
        .paper-sheet { background:#fff; color:#0A0A0A; box-shadow:0 10px 40px rgba(0,0,0,.4); margin:0 auto; }
        .legal-page { width:8.5in; min-height:11in; padding:0.7in 0.8in; font-family:Georgia,'Times New Roman',serif; font-size:11pt; line-height:1.5; color:#111; }
        .rn-head { text-align:center; }
        .rn-firm { font-size:19pt; font-weight:700; letter-spacing:.18em; color:#0B4636; }
        .rn-firm-sub { font-size:9pt; color:#444; margin-top:3px; }
        .lg-rule { height:2px; background:#0B4636; margin:12px 0 16px; }
        .rn-meta { font-size:10.5pt; line-height:1.6; }
        .rn-block { margin-top:14px; font-size:10.5pt; line-height:1.4; }
        .rn-lbl { font-weight:700; margin-bottom:3px; }
        .rn-re { margin-top:16px; }
        .lg-h { font-weight:700; letter-spacing:.04em; margin:16px 0 6px; color:#0B4636; }
        .lg-ol { padding-left:20px; list-style:decimal outside; }
        .lg-ol > li { margin-bottom:8px; display:list-item; }
        .lg-note { font-size:9.5pt; font-style:italic; color:#555; margin-top:22px; border-top:1px solid #ddd; padding-top:10px; }
        .lg-table { width:100%; border-collapse:collapse; margin-top:8px; font-size:10.5pt; }
        .lg-table td { border:1px solid #d8d8d0; padding:6px 9px; }
        .lg-table td:first-child { background:#f6f6f1; font-weight:600; width:42%; }
        .rn-sig { margin-top:26px; }
        .rn-sig-line { width:280px; border-bottom:1.2px solid #111; margin-bottom:6px; height:26px; }
        .rn-sig-sub { font-size:10pt; color:#555; margin-top:2px; }
      `}</style>
    </div>
  );
}
